import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Inline helper for special/technical terms. Two flavours:
 *
 * - `<TermHelp term="roster">` — renders just a small ⓘ info icon that, on
 *   hover, opens a tooltip with the term's full definition. Use when the
 *   surrounding label already names the term.
 * - `<TermHelp term="roster" inline>Roster</TermHelp>` — wraps a piece of
 *   text with a subtle underline + icon. Hover anywhere on the chunk shows
 *   the definition. Use when you also want the term itself underlined.
 *
 * Definitions live in i18n under `glossary.<term>` with `.label` and
 * `.def` sub-keys.
 */
export function TermHelp({
  term,
  inline,
  children,
  size = "small",
}: {
  term: string;
  inline?: boolean;
  children?: React.ReactNode;
  size?: "small" | "medium";
}) {
  const { t } = useTranslation();
  const definition = t(`glossary.${term}.def`, { defaultValue: "" });
  const label = t(`glossary.${term}.label`, { defaultValue: term });
  const title = definition ? (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
        {label}
      </Typography>
      <Typography variant="caption" component="div" sx={{ mt: 0.25 }}>
        {definition}
      </Typography>
    </Box>
  ) : (
    label
  );

  if (inline) {
    return (
      <Tooltip title={title} arrow placement="top">
        <Box
          component="span"
          sx={{
            borderBottom: "1px dotted",
            borderColor: "text.secondary",
            cursor: "help",
            display: "inline-flex",
            alignItems: "baseline",
            gap: 0.25,
          }}
        >
          {children ?? label}
          <HelpOutlineIcon
            sx={{
              fontSize: size === "small" ? 12 : 14,
              color: "text.secondary",
              position: "relative",
              top: 1,
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={title} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          ml: 0.5,
          cursor: "help",
        }}
      >
        <HelpOutlineIcon
          sx={{
            fontSize: size === "small" ? 14 : 16,
            color: "text.secondary",
            opacity: 0.7,
            "&:hover": { opacity: 1 },
          }}
        />
      </Box>
    </Tooltip>
  );
}
